import React from 'react'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
  InputLabel
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { useFormik } from 'formik'
import { mapStatesToBackendDropdown } from 'helpers/backendStatesMapping'
import { mapStatesToFrontendDropdown } from 'helpers/backendStatesMapping'
import OrbitSession from 'hooks/useCookies'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'
import * as yup from 'yup'

import { CustomerDetailsTextField } from '../components/CustomerDetailsTextField'
const headerMap = {
  accountStatus: 'Account Status'
}

function UserBillingCycleForm(billingCycleProps: BillingCycleProps) {
  const {
    customerRow,
    onSuccessfulFormSubmission,
    handleDialogClose,
    currentBillingDate,
    nextBillingDate
  } = billingCycleProps
  const [dialogSequence, setDialogSequence] = useState(0)
  const [initialValues, setInitialValues] = useState({
    accountStatus: mapStatesToFrontendDropdown(customerRow.planStatus),
    remarks: ''
  })
  const faberToken = OrbitSession.get('faberToken')
  const validationSchema = yup.object({
    remarks: yup.string().required('Remarks are required')
  })

  const handleAccountStatus = () => {
    if (initialValues.accountStatus === 'ACTIVE') {
      formik.setFieldValue('accountStatus', 'UNSUBSCRIBED')
    } else {
      formik.setFieldValue('accountStatus', 'ACTIVE')
    }
  }
  const {
    mutate: updateCustomerDetails,
    isLoading: isUpdateCustomerDetailsLoading
  } = useMutation(SubscriptionsAPI.updateCustomerDetails, {
    onSuccess: (res) => {
      onSuccessfulFormSubmission()
      toast.success('Customer details updated')
      const updatedAccStatus =
        res.data.update && res.data.update[0] === 'RESUBSCRIBED'
          ? 'ACTIVE'
          : 'UNSUBSCRIBED'
      setInitialValues({
        accountStatus: updatedAccStatus,
        remarks: ''
      })
    },
    onError: () => {
      toast.error('Error updating customer details')
    },
    onSettled: () => {
      handleDialogClose()
    }
  })
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      updateCustomerDetails({
        userId: customerRow.userID,
        initPlan: customerRow.planName,
        initAccStatus: customerRow.planStatus,
        initFirstName: customerRow.firstName,
        initLastName: customerRow.lastName,
        initEmail: customerRow.userEmail,
        firstName: customerRow.firstName,
        lastName: customerRow.lastName,
        email: customerRow.userEmail,
        nextPlan: customerRow.planName,
        accStatus: mapStatesToBackendDropdown(values.accountStatus),
        remarks: values.remarks,
        faberToken: faberToken
      })
    },
    validationSchema: validationSchema
  })

  return (
    <>
      {dialogSequence === 0 && (
        <>
          <Grid container gap={1} alignItems='center' sx={{ px: 5 }}>
            <Grid item xs={12}>
              <TextColorFontBody
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  backgroundColor: '#ebedfb',
                  p: 1
                }}
              >
                Current Subscription status : {initialValues.accountStatus}
              </TextColorFontBody>
              <TextColorFontBody sx={{ fontSize: '0.8em', color: '#99a2ae' }}>
                {initialValues.accountStatus === 'ACTIVE'
                  ? 'The following action will unsubscribe customer current subscription'
                  : ' The following action will resubscribe customer current subscription'}
              </TextColorFontBody>
            </Grid>

            <Grid item xs={12}>
              <TextColorFontBody sx={{ fontSize: '16px', fontWeight: 400 }}>
                {currentBillingDate}
              </TextColorFontBody>
              <Divider sx={{ width: '100%' }} />
              <InputLabel sx={{ fontSize: '12px' }}>
                Current Billing Cycle
              </InputLabel>
            </Grid>

            <Grid item xs={12}>
              <TextColorFontBody sx={{ fontSize: '16px', fontWeight: 400 }}>
                {nextBillingDate}
              </TextColorFontBody>
              <Divider sx={{ width: '100%' }} />
              <InputLabel sx={{ fontSize: '12px' }}>
                {' '}
                Next Billing Cycle
              </InputLabel>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <CustomerDetailsTextField
                  variant='standard'
                  id='remarks'
                  multiline
                  value={formik.values.remarks}
                  onChange={(e) => {
                    formik.handleBlur(e)
                    formik.handleChange(e)
                  }}
                  sx={{ minWidth: '520px', resize: 'horizontal' }}
                  InputProps={{
                    sx: {
                      color: '#333',
                      fontSize: '14px'
                    }
                  }}
                  error={
                    formik.touched.remarks && Boolean(formik.errors.remarks)
                  }
                  helperText={formik.touched.remarks && formik.errors.remarks}
                />
                {!(formik.touched.remarks && Boolean(formik.errors.remarks)) ? (
                  <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                    Remarks
                  </InputLabel>
                ) : null}
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: 2 }}>
            <DialogActions>
              <RoundedButton variant='outlined' onClick={handleDialogClose}>
                Cancel
              </RoundedButton>
              <RoundedButton
                disabled={!formik.values.remarks || !formik.isValid}
                variant='contained'
                onClick={() => {
                  handleAccountStatus()
                  setDialogSequence(1)
                }}
                sx={{
                  ':disabled': {
                    border: 'none'
                  }
                }}
              >
                {initialValues.accountStatus === 'ACTIVE'
                  ? 'Unsubscribe'
                  : 'Resubscribe'}
              </RoundedButton>
            </DialogActions>
          </Box>{' '}
        </>
      )}
      {dialogSequence === 1 && (
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            sx={{
              mt: 2,
              height: '5vh',
              padding: '4px 0',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: '#f6f6f6',
              px: 2
            }}
          >
            <Grid item xs={6}>
              <TextColorFontBody
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '14px',
                  textAlign: 'left',
                  ml: 3
                }}
              >
                Current
              </TextColorFontBody>
            </Grid>
            <Grid item xs={6}>
              <TextColorFontBody
                sx={{
                  fontWeight: 'bolder',
                  fontSize: '14px',
                  textAlign: 'left',
                  ml: 3
                }}
              >
                Changes made
              </TextColorFontBody>
            </Grid>
          </Grid>
          <Grid container px={1} mt={2} mb={1}>
            {Object.keys(headerMap).map((fieldName, index) => {
              return (
                <React.Fragment key={fieldName}>
                  <Grid
                    item
                    xs={6}
                    sx={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    <Box ml={4}>{formik.initialValues[fieldName]}</Box>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sx={{ textAlign: 'left', fontSize: '12px' }}
                  >
                    <Box ml={3}>{formik.values[fieldName]}</Box>
                  </Grid>
                  {index !== Object.keys(headerMap).length - 1 && (
                    <Divider sx={{ margin: '8px auto', width: '100%' }} />
                  )}
                </React.Fragment>
              )
            })}
            <Box p={3} sx={{ width: '100%', maxWidth: '500px' }}>
              <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                Remarks
              </InputLabel>
              <CustomerDetailsTextField
                variant='standard'
                id='remarks'
                multiline
                value={formik.values.remarks}
                disabled
                sx={{ minWidth: '520px', resize: 'horizontal', ml: '5px' }}
                InputProps={{
                  sx: {
                    color: '#333',
                    fontSize: '14px'
                  }
                }}
              />
            </Box>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: 2 }}>
            <DialogActions>
              <RoundedButton
                variant='outlined'
                onClick={() => setDialogSequence(0)}
              >
                Back
              </RoundedButton>
              <LoadingButton
                type='submit'
                variant='filled'
                loadingIndicator={
                  <CircularProgress color='inherit' size={16} />
                }
                loading={isUpdateCustomerDetailsLoading}
              >
                Confirm
              </LoadingButton>
            </DialogActions>
          </Box>
        </form>
      )}
    </>
  )
}

export default UserBillingCycleForm

interface BillingCycleProps {
  customerRow: SubscriptionsTableRows
  onSuccessfulFormSubmission: () => void
  handleDialogClose: () => void
  currentBillingDate: string | undefined
  nextBillingDate: string | undefined
}
